// src/features/owner/sections/content/CouncilTeamsSection.tsx
//
// The console surface for head-moderator appointment. A team is drawn headless
// and CANNOT vote until the President or Vice President appoints one of its own
// members to lead its case, so this is the screen that unblocks a case.
//
// Every member is listed, including the ones who cannot be appointed, each with
// the reason. The reason is not guessed here: the server computes it with the
// same function the grant endpoint runs, so a row that offers a control is a row
// whose control will succeed, and a row that does not offers a sentence instead
// of nothing.
import styled from 'styled-components';
import { useOwnerCouncilTeams } from '@/hooks/useOwnerCouncilTeams';
import { useOwnerAction } from '@/hooks/useOwnerAction';
import { Button, MetaRow } from '@/ui';
import { Icon } from '@/icons';
import { OwnerPanel, DataTable, EmptyState } from '../../components';
import type { Column } from '../../components';
import type {
  CouncilTeamBlock,
  HeadEligibility,
  OwnerCouncilMemberRow,
  OwnerCouncilTeamRow,
} from '@/core/models';

/** Every wire code the two endpoints can answer with, as a short human phrase. */
const REFUSAL_PHRASE: Record<HeadEligibility | CouncilTeamBlock, string> = {
  eligible: 'Eligible',
  already_head: 'Leads this team',
  self_action: 'You cannot appoint yourself',
  target_not_active: 'Suspended or banned',
  // Kept although the payload cannot produce it for a rendered row — the read
  // iterates memberships, so every member listed IS on the team. It stays as the
  // safety net for the day the payload starts listing candidates from outside
  // the team; a code with no phrase would render a blank cell.
  not_on_team: 'Not on this team',
  not_a_moderator: 'No longer a Council moderator',
  subject_of_case: 'This case is about them',
  head_already_appointed: 'Another member already leads this team',
  team_dissolved: 'Team dissolved',
  case_closed: 'Case already decided',
};

const ACCOUNT_LABEL: Record<OwnerCouncilMemberRow['status'], string> = {
  active: 'Active',
  suspended: 'Suspended',
  banned: 'Banned',
};

const BLOCK_NOTE: Record<CouncilTeamBlock, string> = {
  team_dissolved:
    'This team was dissolved when its case resolved. It never votes again, and its head stays as the record of who led it, so neither appointing nor removing one is possible.',
  // The server refuses BOTH grant and revoke on a decided case, so say both.
  // "Appointment changes nothing now" reads like a no-op that would be allowed
  // and simply have no effect; it is a 400.
  case_closed:
    'This team’s case is already decided. The server refuses to appoint or remove a head moderator on a decided case, so neither is possible here, and the head stays as the record of who led it.',
};

const TeamCard = styled.section`
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 12px 14px;
  & + & {
    margin-top: 12px;
  }
`;
const TeamHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 6px;
`;
const TeamName = styled.h4`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;
const Meta = styled.p`
  margin: 0 0 10px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;
const Note = styled.p`
  margin: 0 0 10px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  border-left: 2px solid ${({ theme }) => theme.color.glassBorder};
  padding-left: 8px;
`;
const Intro = styled.p`
  margin: 0 0 12px;
  opacity: 0.7;
`;
const Muted = styled.span`
  color: ${({ theme }) => theme.color.textMuted};
`;
const HeadTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 6px;
  color: ${({ theme }) => theme.color.accentCyan};
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

/**
 * What the Appointment column says about this member — the state, not a guess.
 *
 * The precedence is the grant view's own evaluation order, which is NOT the
 * tidy-looking `blocked ?? eligibility`:
 *
 *   isHead  >  self_action  >  team.blocked  >  eligibility
 *
 * `self_action` above `blocked` because the view checks it above
 * `_refuse_on_team_or_case`: your own row on a decided team answers
 * `self_action`, and saying "Case already decided" there would name a rule the
 * server is not applying.
 *
 * `isHead` sits at the top for a different reason — it is not a prediction at
 * all but a fact about the row, and the row that IS the head must not report its
 * appointment state as "You cannot appoint yourself". It is the head; say so.
 * That row's control is a Remove, which has no self-action rule.
 */
function appointmentState(
  team: OwnerCouncilTeamRow,
  member: OwnerCouncilMemberRow,
): HeadEligibility | CouncilTeamBlock {
  if (member.isHead) return 'already_head';
  if (member.eligibility === 'self_action') return 'self_action';
  return team.blocked ?? member.eligibility;
}

/**
 * The case this team is seated on, or why it is seated on nothing.
 *
 * The id is not prefixed with "Case" — the ids are already `case-…`, and
 * "Case case-abc" is a stutter. Raw kebab enums are the house standard here
 * (ReportsSection renders them the same way).
 */
function caseLine(team: OwnerCouncilTeamRow): string[] {
  if (!team.caseId) {
    // An appeal reassigns the case to a freshly drawn team, so a dissolved team
    // can genuinely hold zero cases. Saying "not seated on anything" there, one
    // line above "this team was dissolved when its case resolved", is one card
    // contradicting itself.
    return [
      team.dissolved
        ? 'No case on this team: its case resolved, or an appeal moved it to a freshly drawn team.'
        : 'No case: this team is not seated on anything.',
    ];
  }
  const bits = [team.caseId, team.caseKind, team.caseCategory, team.caseStatus];
  return bits.filter((b): b is string => Boolean(b));
}

/** "Head: Someone Else @mod0", or that there is none. Names, like every other cell. */
function headLine(team: OwnerCouncilTeamRow): string {
  if (!team.head) return 'No head appointed';
  // The display name comes off the member row (the head is always on the team);
  // the handle comes off the payload's own `headHandle`. A bare username here
  // was the one cell in the section that named an account differently from
  // every other cell.
  const seated = team.members.find((m) => m.isHead);
  return `Head: ${seated?.name ?? team.head} ${team.headHandle ?? ''}`.trimEnd();
}

function TeamRows({
  team,
  canAppoint,
  onAppoint,
  onRemove,
}: {
  team: OwnerCouncilTeamRow;
  canAppoint: boolean;
  onAppoint: (team: OwnerCouncilTeamRow, username: string) => void;
  onRemove: (team: OwnerCouncilTeamRow) => void;
}) {
  // N teams means N tables with identical headers. The shared DataTable renders
  // no caption, so each card is a <section> named by its own heading instead —
  // that is what tells a screen-reader user which team a table belongs to.
  const headingId = `council-team-${team.id}`;

  const columns: Column<OwnerCouncilMemberRow>[] = [
    {
      key: 'member',
      header: 'Member',
      sortBy: (r) => r.handle,
      render: (r) => (
        <>
          {r.name} <Muted>{r.handle}</Muted>
          {r.isHead && (
            <HeadTag>
              <Icon name="crown" size={12} />
              Head
            </HeadTag>
          )}
        </>
      ),
    },
    {
      // The account's own state, reported separately from the appointment
      // verdict — a suspended member fails both, and collapsing them would hide
      // which one the operator can actually lift. The Council seat is part of
      // that state and not of the verdict: a HEAD who lost the flag reads
      // "Leads this team" in the appointment column (it is a fact, not a
      // prediction), so this is the only cell that reports it for them.
      key: 'account',
      header: 'Account',
      render: (r) => (
        <MetaRow>
          {ACCOUNT_LABEL[r.status]}
          {!r.isCouncilModerator && <Muted>Not on the Council</Muted>}
        </MetaRow>
      ),
    },
    {
      // Never omitted and never blank: a member who cannot be appointed says so,
      // rather than simply having no button and leaving the operator guessing.
      key: 'appointment',
      header: 'Appointment',
      render: (r) => {
        const code = appointmentState(team, r);
        return code === 'eligible' ? (
          REFUSAL_PHRASE.eligible
        ) : (
          <Muted>{REFUSAL_PHRASE[code]}</Muted>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      render: (r) => {
        if (!canAppoint) return null;
        // REMOVE KEYS OFF `isHead`, NOT off the eligibility code. Two states
        // strand a case otherwise, and the revoke endpoint answers 200 to both:
        //   * the viewer IS the head — their row reads `self_action`, so a
        //     control keyed off `already_head` never appears on it, and every
        //     other row reads `head_already_appointed`. Nobody can stand them
        //     down from the console;
        //   * the head lost the moderator flag (two clicks away in User Roles,
        //     and nothing clears a headship on revoke) — their row reads
        //     `not_a_moderator`, with the same result: a case stuck under an
        //     ineligible head with no exit.
        // Standing down from a headship is not self-dealing, which is why the
        // endpoint has no self-action rule here and this control needs no
        // eligibility check — only the team-level pair, which revoke shares.
        if (r.isHead) {
          if (team.blocked) return null;
          return (
            <Button
              variant="ghost"
              // WCAG 2.5.3: the visible words lead the accessible name, so a
              // speech-input user saying "Remove head" matches this control.
              aria-label={`Remove head moderator of ${team.name}: ${r.username}`}
              onClick={() => onRemove(team)}
            >
              Remove head
            </Button>
          );
        }
        // Anything that is not `eligible` would answer 400, and a control that
        // always fails is worse than the sentence beside it.
        if (appointmentState(team, r) !== 'eligible') return null;
        return (
          <Button
            variant="ghost"
            aria-label={`Appoint head moderator of ${team.name}: ${r.username}`}
            onClick={() => onAppoint(team, r.username)}
          >
            Appoint head
          </Button>
        );
      },
    },
  ];

  return (
    <TeamCard aria-labelledby={headingId}>
      <TeamHead>
        <TeamName id={headingId}>{team.name}</TeamName>
        <Muted>{headLine(team)}</Muted>
      </TeamHead>
      <Meta>
        <MetaRow>
          {caseLine(team)}
          {team.status}
          {`caseload ${team.caseload}`}
        </MetaRow>
      </Meta>
      {team.blocked && <Note>{BLOCK_NOTE[team.blocked]}</Note>}
      <DataTable<OwnerCouncilMemberRow>
        columns={columns}
        rows={team.members}
        rowKey={(r) => r.username}
        emptyIcon="users-round"
        emptyMessage="This team has no members."
      />
    </TeamCard>
  );
}

/**
 * The empty state is the screen this console actually shows today — there are no
 * teams and, on production, no moderators either. So it states the reason rather
 * than reporting an absence: a team is not a standing body, it is drawn per case
 * from the Council pool, and it is dissolved with that case.
 *
 * And it says which side of the threshold the pool falls on. `assemble_team`
 * returns None below it, so a pool of 3 can never have a team drawn for it while
 * a pool of 12 is simply waiting for a case — one sentence for both was the
 * console being less truthful than `manage.py check`, which already reports this
 * as moderation.W001.
 */
function emptyMessage(poolSize: number, threshold: number): string {
  const pool = poolSize === 1 ? '1 active moderator' : `${poolSize} active moderators`;
  const preamble =
    'No Council teams yet. Teams are not standing bodies. One is drawn at random from the Council pool for each case, and dissolved when that case resolves.';
  if (poolSize === 0) {
    return `${preamble} The pool is empty, so no team can be drawn at all: seat moderators on the Council from User Roles first.`;
  }
  if (poolSize < threshold) {
    return `${preamble} The pool holds ${pool}, which is too few to decide a case: a team needs ${threshold} to reach a decision, so no team can be drawn yet. Seat more on the Council from User Roles.`;
  }
  return `${preamble} The pool holds ${pool}, enough to draw a team that can decide (${threshold} are needed), so one will be drawn the next time a case needs it.`;
}

export function CouncilTeamsSection() {
  const { data, loading, error, refetch } = useOwnerCouncilTeams();
  const action = useOwnerAction();

  const teams = data?.results ?? [];
  // Server-computed, and deliberately not re-derived from the session: the
  // session carries `isSuperuser` and no Council office at all, so a client-side
  // gate would either hide controls a President may use or show controls the
  // server answers 403 to.
  const canAppoint = data?.canAppoint ?? false;
  const poolSize = data?.poolSize ?? 0;
  const threshold = data?.threshold ?? 0;

  // Refetch rather than patch: a sanction or a Council decision strips a
  // headship, so the eligibility list is stale the moment anything writes.
  const appoint = async (team: OwnerCouncilTeamRow, username: string) => {
    await action.appointHeadModerator(team.id, team.name, username);
    refetch();
  };
  const remove = async (team: OwnerCouncilTeamRow) => {
    await action.revokeHeadModerator(team.id, team.name);
    refetch();
  };

  // Loading returns BEFORE the panel, as both reference sections do — a panel
  // whose body is the word "Loading…" is chrome around nothing.
  if (loading) return <p style={{ opacity: 0.6 }}>Loading…</p>;

  return (
    // Not "Council Teams": OwnerLayout already renders that as the page <h1>, and
    // repeating it verbatim gives the page two headings with the same accessible
    // name — the tell was a route test that had to disambiguate by level.
    <OwnerPanel title="Teams and head moderators">
      <Intro>
        A team is drawn at random for each case and dissolved when the case resolves. It is drawn
        headless and cannot vote until the President or Vice President appoints one of its own
        members to lead the case, so appointing a head is what releases a case for review.
      </Intro>
      {/* Gated on `data`, not on `canAppoint` alone: with no payload the flag is
          false by default, and a failed fetch would otherwise tell the operator
          their account lacks powers it may well have. */}
      {data && !canAppoint && (
        <Note>
          You can see every team here, but the appointment controls are hidden because this account
          cannot use them: appointing a head moderator requires superuser access{' '}
          <strong>and</strong> the Council President or Vice President office. Either one alone is
          refused by the server.
        </Note>
      )}
      {teams.length === 0 ? (
        // The fetch error goes where the empty state goes, exactly as
        // ReportsSection passes it to DataTable's emptyMessage — so a failed
        // read still renders a section rather than collapsing to two words.
        <EmptyState icon="users-round" message={error ?? emptyMessage(poolSize, threshold)} />
      ) : (
        teams.map((t) => (
          <TeamRows
            key={t.id}
            team={t}
            canAppoint={canAppoint}
            onAppoint={appoint}
            onRemove={remove}
          />
        ))
      )}
    </OwnerPanel>
  );
}
